import { Injectable, Injector } from "@angular/core";
import { UserType } from "@common/ADAPT.Common.Model/embed/user-type";
import { Connection, ConnectionBreezeModel, RoleInOrganisation } from "@common/ADAPT.Common.Model/organisation/connection";
import { ConnectionType } from "@common/ADAPT.Common.Model/organisation/connection-type";
import { Organisation, OrganisationBreezeModel } from "@common/ADAPT.Common.Model/organisation/organisation";
import { Role, RoleBreezeModel } from "@common/ADAPT.Common.Model/organisation/role";
import { CoachRoleTypes } from "@common/ADAPT.Common.Model/organisation/role-type-code";
import { PersonBreezeModel } from "@common/ADAPT.Common.Model/person/person";
import { Practitioner, PractitionerBreezeModel } from "@common/ADAPT.Common.Model/practitioner/practitioner";
import { MethodologyPredicate } from "@common/lib/data/methodology-predicate";
import { BaseService } from "@common/service/base.service";
import { Observable } from "rxjs";
import { map, switchMap } from "rxjs/operators";

@Injectable({
    providedIn: "root",
})
export class CoachService extends BaseService {

    constructor(
        injector: Injector,
    ) {
        super(injector);
    }

    public getAllCoaches() {
        return this.commonDataService.getAll(PractitionerBreezeModel);
    }

    public getCoach(id: number) {
        return this.commonDataService.getById(PractitionerBreezeModel, id).pipe(
            switchMap((coach) => this.commonDataService.getById(PersonBreezeModel, coach!.personId).pipe(
                map(() => coach),
            )),
        );
    }

    public getAllOrganisationsForCoach(coach: Practitioner): Observable<Organisation[]> {
        return this.commonDataService.getAll(OrganisationBreezeModel)
            .pipe(map((organisations) => organisations.filter((org) =>
                coach.person.connections.some((connection) => connection.organisationId === org.organisationId
                    && connection.isCoachConnection()
                    && connection.isActive()))));
    }

    public getAllCoachConnectionsForOrganisation(organisationId: number): Observable<Connection[]> {
        const predicate = new MethodologyPredicate<Connection>("organisationId", "==", organisationId)
            .and(new MethodologyPredicate<Connection>("connectionType", "==", ConnectionType.Coach));

        return this.commonDataService.getByPredicate(ConnectionBreezeModel, predicate);
    }

    public getAllCoachRolesForOrganisation(organisationId: number, forceRemote = false): Observable<Role[]> {
        const predicate = new MethodologyPredicate<Role>("roleType.code", "in", CoachRoleTypes)
            .and(new MethodologyPredicate<Role>("organisationId", "==", organisationId));

        const key = `coachRoles${organisationId}`;
        return this.commonDataService.getWithOptions(RoleBreezeModel, key, {
            predicate,
            navProperty: "roleType",
            forceRemote,
        });
    }

    public endCoachConnection(connection: Connection) {
        const endDate = new Date();
        connection.endDate = endDate;
        return this.commonDataService.saveEntities([connection]); //The roleConnections are automatically updated on the backend.
    }

    public createCoachConnection(organisation: Organisation) {
        const startDate = new Date();

        const connectionDefaults: Partial<Connection> = {
            organisationId: organisation.organisationId,
            connectionType: ConnectionType.Coach,
            userType: UserType.Coach,
            startDate,
            hasAccess: true,
            roleInOrganisation: RoleInOrganisation.Employee,
        };

        // creation of the roleConnection has been moved to coach-connection-dialog
        // return the newly created connection
        return this.commonDataService.create(ConnectionBreezeModel, connectionDefaults);
    }

    public deleteCoachConnection(connection: Connection) {
        return this.commonDataService.remove(connection).pipe(
            switchMap(() => this.saveEntities(connection)),
        );
    }
}
